import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ZodType } from 'zod';

import { changePassword } from '@/api/auth/auth.actions';
import profileIcon from '@/assets/profileIcon.png';
import CustomEntityForm from '@/components/controls/CustomEntityForm';
import { EntityFormFieldSpecification } from '@/components/controls/CustomEntityFormField';
import PasswordEyeIcon from '@/components/controls/images/input/PasswordEyeIcon';
import { ConfirmPasswordSchema } from '@/components/zod/types';
import { useAsync } from '@/hooks/global/useAsync';

type ChangePasswordData = {
  password: string;
  confirmPassword: string;
};

const ChangePasswordForm: FC = () => {
  const navigate = useNavigate();
  const { run, isLoading } = useAsync();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChangePassword = async (
    data: ChangePasswordData
  ): Promise<void> => {
    const response = await run(() => changePassword(data.password));

    if (!response.success) {
      toast.error(response.error.message);
      return;
    }

    toast.success(response.message);
    setTimeout(() => navigate('/'), 1000);
  };

  const changePasswordFields: EntityFormFieldSpecification[] = [
    {
      name: 'password',
      label: 'Password',
      fieldType: 'input',
      type: showPassword ? 'text' : 'password',
      placeholder: 'Enter new password',
      validation: { required: true },
      icon: <PasswordEyeIcon />,
      onIconClick: () => setShowPassword((prev) => !prev),
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      fieldType: 'input',
      type: showConfirmPassword ? 'text' : 'password',
      placeholder: 'Confirm new password',
      validation: { required: true },
      icon: <PasswordEyeIcon />,
      onIconClick: () => setShowConfirmPassword((prev) => !prev),
    },
  ];

  return (
    <CustomEntityForm<ChangePasswordData>
      schema={ConfirmPasswordSchema as ZodType<ChangePasswordData>}
      isLoading={isLoading}
      fields={changePasswordFields}
      initialMode="create"
      onSubmit={handleChangePassword}
      onCancel={() => navigate('/')}
      title={
        <div className="flex items-center justify-center md:justify-start gap-2">
          <img
            src={profileIcon}
            alt="Profile icon"
            className="w-6 h-6 object-contain"
          />
          <span>Change Password</span>
        </div>
      }
    />
  );
};

export default ChangePasswordForm;
